/* eslint-disable @typescript-eslint/no-explicit-any */
import { CartProvider, SessionProvider, UIProvider } from '@faststore/sdk'
import {
  AppInsightsContext,
  AppInsightsErrorBoundary,
} from '@microsoft/applicationinsights-react-js'
import type { GatsbyBrowser } from 'gatsby'
import Layout from 'src/Layout'
import LayoutFullscreen from 'src/LayoutFullscreen'
import { RenderClient } from 'src/components/RenderClient'
import ClerkioScript from 'src/components/ThirdPartyScripts/ClerkioScript'
import DynamicYieldLoginEventHandler from 'src/components/ThirdPartyScripts/DynamicYieldLoginEventHandler'
import SharedCartModal from 'src/components/cart/SharedCartModal'
import { SharedCartProvider } from 'src/contexts/shared-cart-context'
import { WishlistProvider } from 'src/contexts/wishlist-context'
import AnalyticsHandler from 'src/sdk/analytics'
import { validateSharedCart } from 'src/sdk/cart/validate'
import ErrorBoundary from 'src/sdk/error/ErrorBoundary'
import { validateSession } from 'src/sdk/session/validate'
import { uiActions, uiEffects, uiInitialState } from 'src/sdk/ui'
import { ModalProvider } from 'src/sdk/ui/modal'
import './src/styles/global.css'
import './src/styles/global/layout.scss'
import './src/styles/global/resets.scss'
import './src/styles/global/tokens.scss'
import './src/styles/global/typography.scss'

import { Helmet } from 'react-helmet'
import DynamicYieldPageContext from 'src/components/ThirdPartyScripts/DynamicYieldPageContext'
import DynamicYieldScriptLoader from 'src/components/ThirdPartyScripts/DynamicYieldScriptLoader'
import { UserTokenConsentHandler } from 'src/components/algolia/UserTokenConsentHandler'
import { PageTypeProvider } from 'src/contexts/page-type-context'
import { StoreProvider } from 'src/contexts/store-context'
import { UserAccountProvider } from 'src/contexts/user-account-context'
import { ApplicationInsightsService } from 'src/sdk/insights/ApplicationInsightsService'
import {
  getDynamicYieldCategoryPath,
  getDynamicYieldProductSku,
} from 'src/utils/getDynamicYieldPageData'
import { getPageType } from 'src/utils/getPageType'
import storeConfig from './store.config'

export const wrapRootElement: GatsbyBrowser['wrapRootElement'] = ({
  element,
}) => (
  <ErrorBoundary>
    <AnalyticsHandler />
    <UIProvider
      initialState={uiInitialState}
      actions={uiActions}
      effects={uiEffects}
    >
      <SessionProvider
        initialState={{
          channel: storeConfig.session.channel,
          locale: storeConfig.session.locale,
          currency: storeConfig.session.currency,
          country: storeConfig.session.country,
        }}
        onValidateSession={validateSession}
      >
        <CartProvider mode="optimistic" onValidateCart={validateSharedCart}>
          <SharedCartProvider>
            <ModalProvider>
              <StoreProvider>
                <UserAccountProvider>
                  {element}
                  {/* Caricato una volta nel browser dopo l'idratazione. Il loader
                      scarica gli script DY; quegli script poi iniettano markup
                      campagna negli slot DOM corrispondenti. */}
                  <DynamicYieldLoginEventHandler />
                  <DynamicYieldScriptLoader />
                  <UserTokenConsentHandler />
                  <RenderClient>
                    <SharedCartModal />
                  </RenderClient>
                </UserAccountProvider>
              </StoreProvider>
            </ModalProvider>
          </SharedCartProvider>
        </CartProvider>
      </SessionProvider>
    </UIProvider>
  </ErrorBoundary>
)

export const wrapPageElement: GatsbyBrowser['wrapPageElement'] = ({
  element,
  props,
}) => {
  // Fase di renderizzazione client Gatsby per ogni pagina: qui raccogliamo
  // metadati pagina e montiamo DynamicYieldPageContext. Non carichiamo script DY
  // in questo punto.
  const pathname = props?.location?.pathname ?? props?.path ?? '/'
  const productSku = getDynamicYieldProductSku(props)
  const categoryPath = getDynamicYieldCategoryPath(props, pathname)
  const pageType = getPageType(props?.pageResources?.page?.componentChunkName)

  const appInsightService = ApplicationInsightsService(pageType)
  // Check if the page is Institutional for mobile app
  const isInstitutionalForApp = props?.pageContext?.isInstitutionalForApp
  // If it is, return the element without the layout
  if (isInstitutionalForApp) {
    return (
      <>
        {element}
        <DynamicYieldPageContext
          pathname={pathname}
          productSku={productSku}
          categoryPath={categoryPath}
          pageType={pageType}
        />
        <RenderClient>
          <ClerkioScript />
        </RenderClient>
      </>
    )
  }

  const node = props?.data?.cmsFlyerMobile

  if (node) {
    // @ts-expect-error config throw error
    if (node?.config?.slugAndFilterConfig?.noLayout) {
      return <>{element}</>
    }
  }
  const nodeFull = props?.data?.cmsFlyerFullScreen

  if (nodeFull) {
    // @ts-expect-error config throw error
    if (nodeFull?.config?.slugAndFilterConfig?.noLayout) {
      return <LayoutFullscreen>{element}</LayoutFullscreen>
    }
  }

  const nodeBooxieMobilePage = props?.data?.cmsBooxieMobilePage
  if (nodeBooxieMobilePage) {
    const externalScriptUrls: string[] =
      // @ts-expect-error config throw error
      nodeBooxieMobilePage.config?.slugAndFilterConfig?.externalScripts ?? []
    // @ts-expect-error config throw error
    if (nodeBooxieMobilePage?.config?.slugAndFilterConfig?.noLayout) {
      return (
        <>
          {externalScriptUrls.length &&
            externalScriptUrls.map((scriptSrc: string) => (
              <Helmet key={scriptSrc}>
                <script src={scriptSrc}></script>
              </Helmet>
            ))}
          {element}
          <DynamicYieldPageContext
            pathname={pathname}
            productSku={productSku}
            categoryPath={categoryPath}
            pageType={pageType}
          />
        </>
      )
    }

    return (
      <>
        {externalScriptUrls.length &&
          externalScriptUrls.map((scriptSrc: string) => (
            <Helmet key={scriptSrc}>
              <script src={scriptSrc} />
            </Helmet>
          ))}
        <Layout>
          {element}
          {/* Tiene window.DY.recommendationContext allineato alla pagina Gatsby
              corrente. E' separato dal caricamento script DY e non renderizza
              UI visibile. */}
          <DynamicYieldPageContext
            pathname={pathname}
            productSku={productSku}
            categoryPath={categoryPath}
            pageType={pageType}
          />
        </Layout>
      </>
    )
  }

  return (
    <AppInsightsErrorBoundary
      onError={() => <h1>I believe something went wrong</h1>}
      appInsights={appInsightService.reactPlugin}
    >
      <AppInsightsContext.Provider value={appInsightService.reactPlugin}>
        <PageTypeProvider
          pageType={getPageType(props?.pageResources?.page?.componentChunkName)}
        >
          <WishlistProvider>
            <Layout>
              {element}
              {/* Tiene window.DY.recommendationContext allineato alla pagina
                  Gatsby corrente. E' separato dal caricamento script DY e non
                  renderizza UI visibile. */}
              <DynamicYieldPageContext
                pathname={pathname}
                productSku={productSku}
                categoryPath={categoryPath}
                pageType={pageType}
              />
              <RenderClient>
                <ClerkioScript />
              </RenderClient>
            </Layout>
          </WishlistProvider>
        </PageTypeProvider>
      </AppInsightsContext.Provider>
    </AppInsightsErrorBoundary>
  )
}

export const onInitialClientRender = () => {
  // Signal that the app is ready for Cypress tests
  document.body.dataset.testReady = 'true'
}

export const onClientEntry: GatsbyBrowser['onClientEntry'] = () => {
  const originalPushState = window.history.pushState.bind(window.history)
  const originalReplaceState = window.history.replaceState.bind(window.history)

  window.history.pushState = (
    data: unknown,
    unused: string,
    url?: string | URL | null
  ): void => {
    if (url != null) {
      const target = new URL(url, window.location.href).href

      // Same URL as the current one: replace instead of adding a duplicate entry
      if (target === window.location.href) {
        originalReplaceState(data, unused, url)
        return
      }
    }

    originalPushState(data, unused, url)
  }
}
